import api from './api';

const MS_POR_DIA = 1000 * 60 * 60 * 24;

// ─── Caché a nivel de módulo ───────────────────────────────────────────────
// Persiste entre montajes/desmontajes de componentes (los 4 tabs del programa
// comparten esta caché). Se invalida cuando el usuario guarda avance, importa
// un XML nuevo, cambia de versión o cambia de proyecto.
let _cachePrograma = null;
let _promesaEnCurso = null;

export const invalidarCachePrograma = () => {
  _cachePrograma = null;
  _promesaEnCurso = null;
};

export const obtenerPrograma = async () => {
  if (_cachePrograma) return _cachePrograma;
  if (_promesaEnCurso) return _promesaEnCurso;

  _promesaEnCurso = api.get('/programa')
    .then(response => {
      _cachePrograma = response.data;
      _promesaEnCurso = null;
      return _cachePrograma;
    })
    .catch(error => {
      _promesaEnCurso = null;
      console.error('Error al obtener programa:', error);
      throw error;
    });

  return _promesaEnCurso;
};

// ─── Detección de hojas O(n) ───────────────────────────────────────────────
// Pre-construye un Set de códigos WBS que son padres (tienen hijos).
// Una actividad es hoja si su wbsCode NO está en ese Set.
// Reemplaza el algoritmo O(n²) anterior (startsWith por cada par).
export function construirHojas(actividades) {
  const wbsSet = new Set(actividades.map(a => a.wbsCode).filter(Boolean));
  const parents = new Set();
  for (const code of wbsSet) {
    const parts = code.split('.');
    for (let i = 1; i < parts.length; i++) {
      parents.add(parts.slice(0, i).join('.'));
    }
  }
  return actividades.filter(a => !a.wbsCode || !parents.has(a.wbsCode));
}

// ─── Proyección de término por cumplimiento de fechas ──────────────────────
// Sin ritmo/velocidad: el atraso de un alcance es el atraso (en días) de su
// partida pendiente más atrasada respecto a su propia fecha base de término.
// hojas: actividades hoja (sin hijos) del alcance a proyectar (global, sección o subgrupo)
export function calcularProyeccionFin(hojas, hoy) {
  if (!hojas.length) return { fechaEstimada: null, diasRetraso: null };
  let maxFin = null, diasRetraso = 0, hayFechas = false;
  for (const a of hojas) {
    if (!a.fechaFinBase) continue;
    hayFechas = true;
    const fin = new Date(a.fechaFinBase);
    if (!maxFin || fin > maxFin) maxFin = fin;
    const avReal = a.porcentajeAvance || 0;
    if (avReal < 100 && fin < hoy) {
      const atraso = Math.round((hoy - fin) / MS_POR_DIA);
      if (atraso > diasRetraso) diasRetraso = atraso;
    }
  }
  if (!hayFechas || !maxFin) return { fechaEstimada: null, diasRetraso: null };

  const fechaEstimada = new Date(maxFin.getTime() + diasRetraso * MS_POR_DIA);
  return { fechaEstimada, diasRetraso };
}

export const obtenerVersiones = async () => {
  try {
    const response = await api.get('/programa/versiones');
    return response.data;
  } catch (error) {
    console.error('Error al obtener versiones:', error);
    throw error;
  }
};

export const importarPrograma = async ({ archivo, nombre }) => {
  try {
    const formData = new FormData();
    formData.append('archivo', archivo);
    formData.append('nombre', nombre);
    const response = await api.post('/programa/importar', formData, {
      headers: { 'Content-Type': undefined },
      timeout: 300000 // 5 minutos — archivos grandes con miles de actividades
    });
    invalidarCachePrograma();
    return response.data;
  } catch (error) {
    console.error('Error al importar programa:', error);
    throw error;
  }
};

export const activarVersion = async (programaId) => {
  try {
    const response = await api.put(`/programa/${programaId}/activar`);
    invalidarCachePrograma();
    return response.data;
  } catch (error) {
    console.error('Error al activar versión:', error);
    throw error;
  }
};

export const eliminarVersion = async (programaId) => {
  try {
    const response = await api.delete(`/programa/${programaId}`);
    invalidarCachePrograma();
    return response.data;
  } catch (error) {
    console.error('Error al eliminar versión:', error);
    throw error;
  }
};

export const obtenerActividadesKpi = async () => {
  try {
    const response = await api.get('/programa/actividades-kpi');
    return response.data.actividades || [];
  } catch (error) {
    console.error('Error al obtener actividades KPI:', error);
    return [];
  }
};

// Misma lógica que calcularKPIs en DashboardPrograma.js (proyección por velocidad, ver calcularProyeccionFin)
// Se ejecuta en el browser para usar la timezone local del usuario
export const calcularTerminoEstimado = (actividades) => {
  if (!actividades.length) return null;

  const hojas = construirHojas(actividades);
  if (!hojas.length) return null;

  const fechaSantiago = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
  const hoy = new Date(fechaSantiago + 'T00:00:00-04:00');

  return calcularProyeccionFin(hojas, hoy).fechaEstimada;
};

export const obtenerActividadesParaRecepcion = async () => {
  try {
    const response = await api.get('/programa/actividades-recepcion');
    return response.data.actividades || [];
  } catch (error) {
    console.error('Error al obtener actividades para recepción:', error);
    return [];
  }
};
